import React, { useMemo, useState } from 'react';

import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import { formatNumber, label, numeric } from './reportModel';

export type Column = { key: string; label: string; numeric?: boolean; percentage?: boolean; decimals?: number };
type Props = {
  title: string;
  rows: Array<Record<string, unknown>>;
  columns: Array<Column>;
  onCsv: () => void;
  onSelect?: (row: Record<string, unknown>) => void;
  note?: string;
  exportReady: boolean;
};
export default function BlogTable({ title, rows, columns, onCsv, onSelect, note, exportReady }: Props) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState(columns[0]?.key);
  const [asc, setAsc] = useState(true);
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const column = columns.find((field) => field.key === sort);
        const left = column?.numeric ? numeric(a[sort]) : label(a[sort]);
        const right = column?.numeric ? numeric(b[sort]) : label(b[sort]);
        if (left === null) {
          return right === null ? 0 : 1;
        }
        if (right === null) {
          return -1;
        }
        return (
          (typeof left === 'number' && typeof right === 'number'
            ? left - right
            : String(left).localeCompare(String(right))) * (asc ? 1 : -1)
        );
      }),
    [rows, columns, sort, asc],
  );
  const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1));
  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        sx={{
          p: 0,
          minHeight: 25,
          bgcolor: '#0f464f',
          color: '#fff',
          '& .MuiButton-root': { color: '#fff', fontSize: 11, p: '3px 6px', minWidth: 0 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              p: '5px 6px',
              minHeight: 25,
              textAlign: 'center',
              color: 'inherit',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Button size="small" onClick={onCsv} disabled={!exportReady || !rows.length}>
          CSV
        </Button>
      </Stack>
      <TableContainer sx={{ maxHeight: 610 }} tabIndex={0} aria-label={`${title}, scroll to see every column`}>
        <Table size="small" stickyHeader aria-label={title}>
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                bgcolor: '#f3f1ea',
                color: '#0f0c08',
                fontSize: 10,
                lineHeight: 1.2,
                p: '5px 4px',
                borderColor: '#d9ddd8',
                fontWeight: 600,
              },
              '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-root:hover': {
                color: 'inherit',
              },
              '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.numeric ? 'right' : 'left'}
                  sortDirection={sort === column.key ? (asc ? 'asc' : 'desc') : false}
                >
                  <TableSortLabel
                    active={sort === column.key}
                    direction={asc ? 'asc' : 'desc'}
                    onClick={() => {
                      setAsc(sort === column.key ? !asc : true);
                      setSort(column.key);
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.slice(safePage * pageSize, (safePage + 1) * pageSize).map((row, index) => (
              <TableRow key={index} hover sx={{ bgcolor: index % 2 ? '#f3f1ea' : '#fff' }}>
                {columns.map((column, columnIndex) => (
                  <TableCell
                    key={column.key}
                    align={column.numeric ? 'right' : 'left'}
                    sx={{
                      p: '3px 4px',
                      fontSize: 10,
                      lineHeight: 1.25,
                      borderColor: '#d9ddd8',
                      minWidth: column.numeric ? 90 : 360,
                      maxWidth: column.numeric ? 130 : 580,
                      fontVariantNumeric: 'tabular-nums',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={label(row[column.key])}
                  >
                    {column.numeric ? (
                      formatNumber(row[column.key], column.percentage, column.decimals)
                    ) : columnIndex === 0 && onSelect ? (
                      <Button
                        size="small"
                        onClick={() => onSelect(row)}
                        sx={{
                          px: 0,
                          py: 0,
                          minHeight: 0,
                          fontSize: 13,
                          lineHeight: 1.4,
                          color: '#555',
                          textTransform: 'none',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                          maxWidth: '100%',
                        }}
                      >
                        {label(row[column.key])}
                      </Button>
                    ) : (
                      label(row[column.key])
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length}>No source rows match these controls.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {note && <Typography sx={{ fontSize: 9, lineHeight: 1.35, color: '#526164', p: '3px 6px' }}>{note}</Typography>}
      <TablePagination
        component="div"
        count={rows.length}
        page={safePage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPage(0);
        }}
        sx={{
          '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', minHeight: 36 },
          '& .MuiTypography-root, & .MuiSelect-select': { fontSize: 10 },
        }}
      />
    </Paper>
  );
}
